import { ref, onMounted, onUnmounted } from "vue";

/**
 * Reactive "is the viewport phone-sized" flag, keyed to Tailwind's `md`
 * breakpoint (768px) by default.
 *
 * This deliberately tracks the available *width* rather than the device
 * identity, so it also reacts to a narrow desktop browser window and to device
 * emulation in dev tools. The two existing checks do neither:
 *   - `useNuxtApp().$deviceType` is parsed from the user agent, so a resized
 *     desktop browser still reports "desktop".
 *   - `DesktopIntegration.isDesktopApp()` only reports whether we are running
 *     inside the Electron shell, which is false for every browser tab.
 */
export function useIsMobile(maxWidth = 767) {
  const query = `(max-width: ${maxWidth}px)`;

  // Seeded synchronously so the first render is already correct (the app runs
  // with `ssr: false`, so `window` is available here).
  const isMobile = ref(
    typeof window !== "undefined" && window.matchMedia(query).matches,
  );

  let mql: MediaQueryList | null = null;
  const update = (e: MediaQueryListEvent | MediaQueryList) => {
    isMobile.value = e.matches;
  };

  onMounted(() => {
    mql = window.matchMedia(query);
    update(mql);
    mql.addEventListener("change", update);
  });

  onUnmounted(() => {
    mql?.removeEventListener("change", update);
    mql = null;
  });

  return isMobile;
}
